/**
 * Office.js PowerPoint host helpers, ported from the prototype
 * (`aiva-outlook-addin/src/taskpane/taskpane.js`):
 *   - getSelectedSlideText / getAllSlidesText: read slide text
 *   - getSelectedSlideImage: base64 PNG of the active slide (Vision input)
 *   - insertTextOnSlide: current_textbox / replace_selection / new_slide / notes
 *   - insertImageOnSlide: placement-based image insert
 *   - getSpeakerNotes / setSpeakerNotes
 *   - getSlideContext: name + count + current index (used by modules to gate UI)
 *
 * All helpers assume the host is PowerPoint; they return safe defaults
 * (empty strings, false) when the host doesn't expose the API rather than
 * throwing — UI code decides whether to surface a notice.
 */

import { logOfficeError } from "../../../telemetry";

type PlacementSlot =
  | "top-left"
  | "top-center"
  | "top-right"
  | "center-left"
  | "center"
  | "center-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export type InsertTextMode =
  | "current_textbox"
  | "replace_selection"
  | "new_slide"
  | "notes";

export interface SlideContext {
  name: string;
  slideCount: number;
  currentSlide: number;
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

export async function getSelectedSlideText(): Promise<string> {
  if (!hasPpt()) return "";
  try {
    return await new Promise<string>((resolve) => {
      ppt()
        .run(async (context) => {
          const selected = context.presentation.getSelectedSlides();
          selected.load("items");
          await context.sync();
          if (selected.items.length === 0) {
            resolve("");
            return;
          }
          const shapes = selected.items[0]!.shapes;
          shapes.load("items");
          await context.sync();
          const texts: string[] = [];
          for (const shape of shapes.items) {
            try {
              const tf = shape.textFrame;
              tf.load("textRange/text");
              await context.sync(); // NOSONAR ordered Office.js operations on a shared context
              if (tf.textRange?.text) texts.push(tf.textRange.text);
            } catch {
              /* shape may not have a textFrame */
            }
          }
          resolve(texts.join("\n"));
        })
        .catch(() => resolve(""));
    });
  } catch {
    return "";
  }
}

export async function getAllSlidesText(): Promise<string> {
  if (!hasPpt()) return "";
  try {
    return await new Promise<string>((resolve) => {
      ppt()
        .run(async (context) => {
          const slides = context.presentation.slides;
          slides.load("items");
          await context.sync();
          const all: string[] = [];
          for (let s = 0; s < slides.items.length; s++) {
            const slide = slides.items[s]!;
            slide.shapes.load("items");
            await context.sync(); // NOSONAR ordered Office.js operations on a shared context
            const slideTexts: string[] = [];
            for (const shape of slide.shapes.items) {
              try {
                const tf = shape.textFrame;
                tf.load("textRange/text");
                await context.sync(); // NOSONAR ordered Office.js operations on a shared context
                if (tf.textRange?.text) slideTexts.push(tf.textRange.text);
              } catch {
                /* skip */
              }
            }
            if (slideTexts.length > 0) {
              all.push(`--- Slide ${s + 1} ---\n${slideTexts.join("\n")}`);
            }
          }
          resolve(all.join("\n\n"));
        })
        .catch(() => resolve(""));
    });
  } catch {
    return "";
  }
}

export async function getSelectedSlideImage(): Promise<string> {
  if (!hasPpt()) throw new Error("PowerPoint host unavailable");
  return new Promise<string>((resolve, reject) => {
    ppt()
      .run(async (context) => {
        const selected = context.presentation.getSelectedSlides();
        selected.load("items");
        await context.sync();
        if (selected.items.length === 0) {
          reject(new Error("No slide selected"));
          return;
        }
        const slide = selected.items[0]!;
        const slideAny = slide as unknown as {
          getImageAsBase64?: (opts: { height: number; width: number }) => OfficeExtension.ClientResult<string>;
        };
        if (typeof slideAny.getImageAsBase64 !== "function") {
          reject(
            new Error(
              "Slide image capture requires PowerPointApi 1.5+ (use PowerPoint Web or update desktop).",
            ),
          );
          return;
        }
        const img = slideAny.getImageAsBase64({ height: 720, width: 960 });
        await context.sync();
        resolve(img.value);
      })
      .catch(reject);
  });
}

export async function getSlideContext(): Promise<SlideContext> {
  const empty: SlideContext = { name: "", slideCount: 0, currentSlide: 1 };
  if (!hasPpt()) return empty;
  try {
    return await new Promise<SlideContext>((resolve) => {
      ppt()
        .run(async (context) => {
          const presentation = context.presentation;
          const slides = presentation.slides;
          slides.load("items/id");
          const selected = presentation.getSelectedSlides();
          selected.load("items/id");
          await context.sync();
          const currentId = selected.items[0]?.id;
          const currentIndex = currentId
            ? slides.items.findIndex((s) => s.id === currentId)
            : -1;
          resolve({
            name: Office.context.document.url ?? "",
            slideCount: slides.items.length,
            currentSlide: currentIndex >= 0 ? currentIndex + 1 : 1,
          });
        })
        .catch(() => resolve(empty));
    });
  } catch {
    return empty;
  }
}

export async function insertTextOnSlide(
  text: string,
  mode: InsertTextMode = "current_textbox",
): Promise<boolean> {
  if (!hasPpt()) return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  try {
    if (mode === "replace_selection") {
      await ppt().run(async (ctx) => {
        const selected = ctx.presentation.getSelectedSlides();
        selected.load("items/id");
        await ctx.sync();
        const slide = selected.items[0];
        if (!slide) throw new Error("No slide selected");
        slide.shapes.load("items/id,items/textFrame/hasText");
        await ctx.sync();
        let target = slide.shapes.items.find(
          (sh) => sh.textFrame?.hasText,
        );
        if (!target && slide.shapes.items.length > 0) target = slide.shapes.items[0];
        if (!target) throw new Error("No shape to replace");
        target.textFrame.textRange.text = trimmed;
        await ctx.sync();
      });
      return true;
    }

    if (mode === "new_slide") {
      await ppt().run(async (ctx) => {
        const lines = trimmed.split(/\r?\n/);
        const titleLine = lines[0] ?? "New Slide";
        const bodyLines = lines.slice(1).join("\n").trim();
        const p = ctx.presentation;
        p.slides.load("items/id");
        await ctx.sync();
        p.slides.add();
        await ctx.sync();
        const count = p.slides.getCount();
        await ctx.sync();
        const newSlide = p.slides.getItemAt(count.value - 1);
        await ctx.sync();
        const titleBox = newSlide.shapes.addTextBox(titleLine, {
          left: 50,
          top: 30,
          width: 860,
          height: 60,
        });
        try {
          titleBox.textFrame.textRange.font.size = 28;
          titleBox.textFrame.textRange.font.bold = true;
        } catch {
          /* font API may be unavailable */
        }
        if (bodyLines) {
          const bodyBox = newSlide.shapes.addTextBox(bodyLines, {
            left: 50,
            top: 110,
            width: 860,
            height: 380,
          });
          try {
            bodyBox.textFrame.textRange.font.size = 18;
          } catch {
            /* skip */
          }
        }
        await ctx.sync();
      });
      return true;
    }

    if (mode === "notes") {
      return await setSpeakerNotes(trimmed, "append");
    }

    // current_textbox — append a fresh text box below existing content.
    await ppt().run(async (ctx) => {
      const selected = ctx.presentation.getSelectedSlides();
      selected.load("items");
      await ctx.sync();
      const slide = selected.items[0];
      if (!slide) throw new Error("No slide selected");
      const box = slide.shapes.addTextBox(trimmed, {
        left: 50,
        top: 240,
        width: 860,
        height: 200,
      });
      try {
        box.textFrame.textRange.font.size = 18;
      } catch {
        /* skip */
      }
      await ctx.sync();
    });
    return true;
  } catch (e) {
    console.error("[ppt-insert] failed:", e);
    logOfficeError(e, "ppt-insert");
    return false;
  }
}

export async function getSpeakerNotes(): Promise<string> {
  if (!hasPpt()) return "";
  try {
    return await new Promise<string>((resolve) => {
      ppt()
        .run(async (ctx) => {
          const selected = ctx.presentation.getSelectedSlides();
          selected.load("items/id");
          await ctx.sync();
          const slide = selected.items[0];
          if (!slide) {
            resolve("");
            return;
          }
          const slideAny = slide as unknown as {
            notesPage?: { shapes: PowerPoint.ShapeCollection };
          };
          if (!slideAny.notesPage?.shapes) {
            resolve("");
            return;
          }
          slideAny.notesPage.shapes.load("items/textFrame/textRange/text");
          await ctx.sync();
          const notesShape = slideAny.notesPage.shapes.items.find(
            (sh) => sh.textFrame?.textRange,
          );
          resolve(notesShape?.textFrame.textRange.text ?? "");
        })
        .catch(() => resolve(""));
    });
  } catch {
    return "";
  }
}

export async function setSpeakerNotes(
  notesText: string,
  mode: "append" | "replace" = "append",
): Promise<boolean> {
  if (!hasPpt()) return false;
  const trimmed = notesText.trim();
  if (!trimmed) return false;
  try {
    await ppt().run(async (ctx) => {
      const selected = ctx.presentation.getSelectedSlides();
      selected.load("items/id");
      await ctx.sync();
      const slide = selected.items[0];
      if (!slide) throw new Error("No slide selected");
      const slideAny = slide as unknown as {
        notesPage?: { shapes: PowerPoint.ShapeCollection };
      };
      if (!slideAny.notesPage?.shapes) {
        throw new Error("Notes API unavailable on this PowerPoint host");
      }
      slideAny.notesPage.shapes.load("items/textFrame/textRange/text");
      await ctx.sync();
      const notesShape = slideAny.notesPage.shapes.items.find(
        (sh) => sh.textFrame?.textRange,
      );
      if (!notesShape) throw new Error("No notes shape on slide");
      const existing = notesShape.textFrame.textRange.text ?? "";
      const combined =
        mode === "append" && existing
          ? `${existing.trimEnd()}\n\n${trimmed}`
          : trimmed;
      notesShape.textFrame.textRange.text = combined;
      await ctx.sync();
    });
    return true;
  } catch (e) {
    console.error("[ppt-notes] failed:", e);
    logOfficeError(e, "ppt-notes");
    return false;
  }
}

const SLIDE_W = 960;
const SLIDE_H = 540;
const PAD = 40;
const DEFAULT_IMAGE_SIZE = { width: 200, height: 200 } as const;

export function calculatePlacement(
  slot: PlacementSlot,
  width: number,
  height: number,
): { left: number; top: number } {
  const map: Record<PlacementSlot, { left: number; top: number }> = {
    "top-left": { left: PAD, top: PAD },
    "top-center": { left: (SLIDE_W - width) / 2, top: PAD },
    "top-right": { left: SLIDE_W - width - PAD, top: PAD },
    "center-left": { left: PAD, top: (SLIDE_H - height) / 2 },
    center: { left: (SLIDE_W - width) / 2, top: (SLIDE_H - height) / 2 },
    "center-right": {
      left: SLIDE_W - width - PAD,
      top: (SLIDE_H - height) / 2,
    },
    "bottom-left": { left: PAD, top: SLIDE_H - height - PAD },
    "bottom-center": {
      left: (SLIDE_W - width) / 2,
      top: SLIDE_H - height - PAD,
    },
    "bottom-right": {
      left: SLIDE_W - width - PAD,
      top: SLIDE_H - height - PAD,
    },
  };
  return map[slot];
}

export async function insertImageOnSlide(
  imageBase64: string,
  placement: PlacementSlot = "center",
  size: { width: number; height: number } = DEFAULT_IMAGE_SIZE,
): Promise<boolean> {
  if (!hasPpt()) return false;
  try {
    await new Promise<void>((resolve, reject) => {
      Office.context.document.setSelectedDataAsync(
        imageBase64,
        {
          coercionType: Office.CoercionType.Image,
          imageWidth: size.width,
          imageHeight: size.height,
        },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
          else reject(new Error(result.error.message));
        },
      );
    });
    const pos = calculatePlacement(placement, size.width, size.height);
    await ppt().run(async (ctx) => {
      const selected = ctx.presentation.getSelectedSlides();
      selected.load("items");
      await ctx.sync();
      const slide = selected.items[0];
      if (!slide) return;
      slide.shapes.load("items/id");
      await ctx.sync();
      const last = slide.shapes.items[slide.shapes.items.length - 1];
      if (!last) return;
      try {
        const shapeAny = last as unknown as {
          left?: number;
          top?: number;
          width?: number;
          height?: number;
        };
        shapeAny.left = pos.left;
        shapeAny.top = pos.top;
        shapeAny.width = size.width;
        shapeAny.height = size.height;
      } catch {
        /* host may not allow direct repositioning */
      }
      await ctx.sync();
    });
    return true;
  } catch (e) {
    console.error("[ppt-image] failed:", e);
    logOfficeError(e, "ppt-image");
    return false;
  }
}
